import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Toggle } from '../../../../components/forms/Toggle';
import { Tooltip } from '../../../../components/actions/Tooltip';
import { PostEditorContext } from '../../states/PostEditorProvider';
import { ToggleAction } from '../../../../components/forms/Toggle/Toggle.controller';
import { ExpireAfterDate } from '../ExpireAfterDate';
import { ExpireAfterCount } from '../ExpireAfterCount';
import { PostEditorGroupDataContext } from '../../states/PostEditorGroupDataProvider';
import { useDidMount } from '../../../../hooks/useDidMount';
import { FormElementAction } from '../../../../models/basic-objects';
import { ActiveCustomizeIndexContext } from '../../states/ActiveCustomizeIndexProvider';
import { addSeconds, isEqual, parse } from 'date-fns';
import { DateStringFormat, DateTimeStringFormat } from '../../../../constants/time-formats';
import { hasSeverity } from 'modules/PostEditor/validators/helpers/severity-checker';
import { ErrorMessageSeverity } from 'constants/form';
import { getErrorMessages } from '../../validators/helpers/getErrorMessages';
import { CheckboxWithAction } from '../../../../components/actions/CheckboxWithAction';
import { PostTiming } from '../../../../models/graphql';
import classes from './EvergreenRecycle.module.scss';
import { getCustomizationProfileId } from '../../validators/helpers/customization-getter';
import { formatErrorMessages } from '../../validators/helpers/formatErrorMessages';
import { GroupPostAttributesSchedulingType } from '../../../../models/post-editor';
import scssVars from '../../../../assets/scss/exportedVars.module.scss';
import { BeaconContext } from '../../../../providers/BeaconProvider';

export type EvergreenRecycleViewProps = {
  //
  formInstance: UseFormReturn;
  type: 'recurring' | 'evergreen';
  hasTextOrMediaVariationError?: boolean;
};

const getMinDate = (scheduling: GroupPostAttributesSchedulingType): string | undefined => {
  const { evergreen, postTiming, recurring, scheduledTimezone } = scheduling;
  const dateToPostSchedule = scheduling.scheduledLocalDateTimes.filter(date => !date.isPast).at(-1);
  const dateToPostRecurring = scheduling.recurring.date;
  const now = addSeconds(new Date(), 60);
  if (
    postTiming === PostTiming.SCHEDULE &&
    dateToPostSchedule &&
    evergreen.expiryDate &&
    evergreen.active &&
    scheduledTimezone
  ) {
    return parse(dateToPostSchedule.dateTime, DateTimeStringFormat, now).toISOString(); // 22.11. 22:15 (CET)
  } else if (postTiming === PostTiming.RECURRING && dateToPostRecurring && recurring.expiryDate && recurring.active) {
    return parse(dateToPostRecurring, DateTimeStringFormat, new Date()).toISOString();
  }
  return undefined;
};

export const EvergreenRecycleView = ({ formInstance, type }: EvergreenRecycleViewProps): JSX.Element => {
  const { state, dispatch } = useContext(PostEditorContext);
  const { beacon } = useContext(BeaconContext);
  const customizeIndex = useContext(ActiveCustomizeIndexContext);
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  const { postGroups, loading } = state;
  const { posts, errors, customizeSchedulePerProfile } = postGroups[postGroupIndex];
  const ref = useRef<HTMLDivElement>(null);
  const { setValue: setFormValue, getValues: getFormValues } = formInstance;
  const isCustomizingSchedule = typeof customizeIndex === 'number' && customizeSchedulePerProfile;
  const customizationProfileId = getCustomizationProfileId(postGroups[postGroupIndex], customizeIndex);
  const scheduling = isCustomizingSchedule
    ? posts[customizeIndex].scheduling
    : postGroups[postGroupIndex].groupPostAttributes.scheduling;
  const postTiming = isCustomizingSchedule
    ? posts[customizeIndex].scheduling.postTiming
    : postGroups[postGroupIndex].groupPostAttributes.scheduling.postTiming;
  const expireType = isCustomizingSchedule
    ? posts[customizeIndex].scheduling[type].expireType
    : postGroups[postGroupIndex].groupPostAttributes.scheduling[type].expireType;
  const expireAfterCount = isCustomizingSchedule
    ? posts[customizeIndex].scheduling[type].expiryMaxRepeats
    : postGroups[postGroupIndex].groupPostAttributes.scheduling[type].expiryMaxRepeats;
  const expireAfterDate = isCustomizingSchedule
    ? posts[customizeIndex].scheduling[type].expiryDate
    : postGroups[postGroupIndex].groupPostAttributes.scheduling[type].expiryDate;
  const active = isCustomizingSchedule
    ? posts[customizeIndex].scheduling[type].active
    : postGroups[postGroupIndex].groupPostAttributes.scheduling[type].active;
  const evergreenToggleName = isCustomizingSchedule
    ? `postGroup.${postGroupIndex}.customize.${customizeIndex}.${type}`
    : `postGroup.${postGroupIndex}.${type}`;
  const expireAfterDateName = isCustomizingSchedule
    ? `postGroup.${postGroupIndex}.customize.${customizeIndex}.${type}.expireAfterDate`
    : `postGroup.${postGroupIndex}.${type}.expireAfterDate`;
  const expireAfterCountName = isCustomizingSchedule
    ? `postGroup.${postGroupIndex}.customize.${customizeIndex}.${type}.expireAfterCount`
    : `postGroup.${postGroupIndex}.${type}.expireAfterCount`;
  const expireTypeName = isCustomizingSchedule
    ? `postGroup.${postGroupIndex}.customize.${customizeIndex}.${type}.expireType`
    : `postGroup.${postGroupIndex}.${type}.expireType`;
  const watchExpireCountValue: number | undefined = formInstance.watch(
    expireAfterCountName,
    expireAfterCount ?? undefined
  );
  const watchExpireDateValue: Date | null = formInstance.watch(expireAfterDateName, undefined);
  const onExpireUpdate: FormElementAction<Date> = useCallback(
    (date, complete) => {
      complete();
      dispatch({
        type: 'setEvergreenExpireAfterDate',
        payload: {
          date,
          postGroupIndex,
          customizeIndex,
          type
        }
      });
    },
    [postGroupIndex, customizeIndex, type, dispatch]
  );

  const stableExpireAfterDate = useMemo(
    () => (expireAfterDate ? parse(expireAfterDate, DateStringFormat, new Date()) : null),
    [expireAfterDate]
  );
  const hasExpiryDateError = hasSeverity(errors, ErrorMessageSeverity.error, customizationProfileId, null, [
    'checkExpiryDate',
    'expireDateRequired'
  ]);
  const hasExpiryDateWarning = hasSeverity(errors, ErrorMessageSeverity.warning, customizationProfileId, null, [
    'checkExpiryDate'
  ]);

  const hasVariationError = hasSeverity(errors, ErrorMessageSeverity.error, customizationProfileId, undefined, [
    'scheduleTextOrMediaVariations'
  ]);
  const hasExpiryCountError = hasSeverity(errors, ErrorMessageSeverity.error, customizationProfileId, null, [
    'checkExpiryCount',
    'expireCountRequired'
  ]);

  const errorExpiryDate = getErrorMessages(errors, customizationProfileId, null, [
    'checkExpiryDate',
    'expireDateRequired'
  ]).join('\n');
  const expiryCountError = getErrorMessages(errors, customizationProfileId, null, [
    'checkExpiryCount',
    'expireCountRequired'
  ]).join('\n');
  const variationError = getErrorMessages(errors, customizationProfileId, undefined, ['scheduleTextOrMediaVariations']);
  const formattedVariationMessages = formatErrorMessages(variationError);

  const minDateStr = getMinDate(scheduling);

  const minDate = useMemo(() => (minDateStr ? new Date(minDateStr) : undefined), [minDateStr]);

  useEffect(() => {
    if (stableExpireAfterDate) {
      const currentFormValue = getFormValues(expireAfterDateName);
      if (!isEqual(currentFormValue, stableExpireAfterDate)) {
        setFormValue(expireAfterDateName, stableExpireAfterDate);
      }
    }
  }, [stableExpireAfterDate, expireAfterDateName, setFormValue, getFormValues]);

  useEffect(() => {
    if (watchExpireCountValue && (typeof expireAfterCount !== 'number' || expireAfterCount !== watchExpireCountValue)) {
      dispatch({
        type: 'setEvergreenExpireAfterCount',
        payload: {
          postGroupIndex,
          counter: +watchExpireCountValue,
          customizeIndex,
          type
        }
      });
    }
  }, [watchExpireCountValue, postGroupIndex, expireAfterCount, type, customizeIndex, dispatch]);

  useEffect(() => {
    if (
      watchExpireDateValue &&
      typeof watchExpireDateValue === 'object' &&
      !isNaN(watchExpireDateValue.getTime()) &&
      (!expireAfterDate || watchExpireDateValue.toLocaleDateString() !== new Date(expireAfterDate).toLocaleDateString())
    ) {
      dispatch({
        type: 'setEvergreenExpireAfterDate',
        payload: {
          postGroupIndex,
          date: watchExpireDateValue,
          customizeIndex,
          type
        }
      });
    }
  }, [watchExpireDateValue, postGroupIndex, expireAfterCount, type, customizeIndex, expireAfterDate, dispatch]);

  useDidMount(() => () => {
    formInstance.unregister([evergreenToggleName, expireAfterDateName, expireAfterCountName, expireTypeName], {
      keepValue: false,
      keepDefaultValue: false
    });
  });

  const toggleAction: ToggleAction = useCallback(
    (value, complete) => {
      complete();
      dispatch({ type: 'toggleEvergreen', payload: { postGroupIndex, customizeIndex, type } });
    },
    [postGroupIndex, customizeIndex, type, dispatch]
  );

  const isHtmlInputElementGuard = (el: EventTarget): el is HTMLInputElement => {
    return el.hasOwnProperty('checked');
  };

  return (
    <div className={'margin-top-small-16 relative'} ref={ref}>
      <div className={'grid-x grid-padding-x'}>
        <div className={'cell shrink'}>
          <div className={'grid-x'}>
            {type === 'evergreen' ? (
              <>
                <div className={'cell shrink'}>
                  <Toggle
                    disabled={state.loading}
                    checked={active}
                    action={toggleAction}
                    formInstance={formInstance}
                    name={evergreenToggleName}
                    rules={{}}
                    label={'Evergreen Recycle'}
                  />
                </div>
                <div className={'cell shrink'}>
                  {!hasVariationError && (
                    <Tooltip
                      pointer
                      target={`#evergreenTooltip_${
                        customizeIndex !== undefined ? customizeIndex : 'group'
                      }_${postGroupIndex}`}
                      type={'inlined'}
                      autoHide={false}
                      style={{ zIndex: scssVars['z-index-overmodal'] }}>
                      <div><p className={'small-text'}>Enable Evergreen Recycling to continuously cycle this Post back to the bottom of your Queue after
                        each publication.</p>
                        <p className={'small-text'}>Recycling frequency depends on how many Posts you have in your Queue, and how many Timeslots you have in the Posting Plan for this Category.</p>
                        <p className={'small-text'}>For manually-scheduled Posts, all scheduled publications occur before recycling begins.</p>
                        <p className={'small-text'}>This Post will continue being recycled indefinitely, unless you set an expiry option, such as a specific date, a set number of
                        repeats, or whichever comes first.</p>
                        <p className={'no-b-m small-text'}><a onClick={() => beacon('article', '689', { type: 'modal' })}>Learn more about Evergreen Recycling</a>.</p></div>
                    </Tooltip>
                  )}
                  {postTiming === PostTiming.QUEUE && hasVariationError ? (
                    <div className={'cell shrink'}>
                      <Tooltip
                        className={classes.Tooltip}
                        icon={'alert'}
                        target={`#hasVariationErrorTooltip_${postGroupIndex}`}
                        position={'right'}
                        pointer={true}
                        type={'inlined'}
                        autoZIndex={false}
                        style={{ zIndex: scssVars['z-index-overmodal'] }}>
                        {formattedVariationMessages}
                      </Tooltip>
                    </div>
                  ) : null}
                </div>
              </>
            ) : (
              <>
                <div className={'cell shrink'}>
                  <p className={classes.ExpiryTitle}>Should this post expire?</p>
                </div>
                <div className={'cell shrink'}>
                  <Tooltip
                    pointer
                    target={`#recurringTooltip_${
                      customizeIndex !== undefined ? customizeIndex : 'group'
                    }_${postGroupIndex}`}
                    type={'inlined'}
                    style={{ zIndex: scssVars['z-index-overmodal'] }}>
                    This post will continue to repeat indefinitely. To set an end point, choose an expiry option: a
                    date, a number of repeats, or whichever comes first.
                  </Tooltip>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {active && (
        <div className={'margin-top-small-16'}>
          <div className={'grid-x align-middle'}>
            <div className={'cell shrink'}>
              <CheckboxWithAction
                checked={expireType.includes('date')}
                value={'date'}
                name={expireTypeName}
                size={'small'}
                label={'Expire after'}
                onClickHandler={event => {
                  if (!isHtmlInputElementGuard(event.target)) return;
                  dispatch({
                    type: 'setEvergreenRecycleType',
                    payload: {
                      postGroupIndex,
                      expireType: 'date',
                      action: event.target.checked ? 'add' : 'remove',
                      customizeIndex,
                      type
                    }
                  });
                }}
                disabled={state.loading}
              />
            </div>
            <div className={'cell shrink'}>
              <ExpireAfterDate
                calendarUpdateAction={onExpireUpdate}
                formInstance={formInstance}
                name={expireAfterDateName}
                disabled={!expireType.includes('date') || loading}
                {...(minDate ? { minDate } : {})}
                {...(stableExpireAfterDate ? { date: stableExpireAfterDate } : {})}
                {...(hasExpiryDateError ? { isError: errorExpiryDate } : {})}
                {...(hasExpiryDateWarning ? { isWarning: errorExpiryDate } : {})}
              />
            </div>
          </div>
          <div className={'grid-x align-middle margin-top-small-16'}>
            <div className={'cell shrink'}>
              <CheckboxWithAction
                checked={expireType.includes('count')}
                value={'count'}
                name={expireTypeName}
                label={'Expire after'}
                size={'small'}
                onClickHandler={event => {
                  if (!isHtmlInputElementGuard(event.target)) return;
                  dispatch({
                    type: 'setEvergreenRecycleType',
                    payload: {
                      postGroupIndex,
                      expireType: 'count',
                      action: event.target.checked ? 'add' : 'remove',
                      customizeIndex,
                      type
                    }
                  });
                }}
                disabled={state.loading}
              />
            </div>
            <div className={'cell shrink'}>
              <ExpireAfterCount
                formInstance={formInstance}
                name={expireAfterCountName}
                disabled={!expireType.includes('count') || loading}
                {...(expireAfterCount ? { value: expireAfterCount } : {})}
                {...(hasExpiryCountError ? { expiryCountError } : {})}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
